import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { memo } from 'react';
import isEqual from 'react-fast-compare';

import { AdContainer } from '@/components/Ads/AdContainer';
import { CompleteAccountBanner } from '@/components/CompleteAccountBanner/CompleteAccountBanner';
import DatePickerFacet from '@/components/Facets/DatePickerFacet';
import { MultiCheckFacet } from '@/components/Facets/MultiCheckFacet';
import { SearchFacetValuesOptions } from '@/redux/modules/auctionCalendar/auctionCalendar.api';
import FacetTypes from '@/types/search/enums/facetTypes';
import { useAverageLotValueActionCalendarRanking } from '@/utils/amplitude/experiments/hooks/useAverageLotValueActionCalendarRanking';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { AuctionCalendarPageHeader } from './AuctionCalendarPageHeader';
import AuctionCalendarPageHeaderMeta from './AuctionCalendarPageHeaderMeta';
import { AuctionCalendarResults } from './AuctionCalendarResults';

type Props = {
    auctionHouses: SearchFacetValuesOptions[];
    baseUrl: string;
    catalogIds: number[];
    catalogsTodayCount: number;
    changePageAndNavigate: Function;
    changePageSizeAndNavigate: Function;
    countries: SearchFacetValuesOptions[];
    dateChanged: Function;
    facetOptionChecked: (facetName: string, value: string, isChecked: boolean) => void;
    liveBroadcastOptions: SearchFacetValuesOptions[];
    page: number;
    pageSize: number;
    pathname: string;
    queryParams: any;
    saleTypes: SearchFacetValuesOptions[];
    searchSubmitted: boolean;
    selectedDate: string;
    shippingOptions: SearchFacetValuesOptions[];
    topRatedAuctionHouses: SearchFacetValuesOptions[];
    totalCatalogsFound: number;
    whiteLabel: boolean;
};

const AuctionCalendarPageComponent = ({
    auctionHouses,
    baseUrl,
    catalogIds,
    catalogsTodayCount,
    changePageAndNavigate,
    changePageSizeAndNavigate,
    countries,
    dateChanged,
    facetOptionChecked,
    liveBroadcastOptions,
    page,
    pageSize,
    pathname,
    queryParams,
    saleTypes,
    searchSubmitted,
    selectedDate,
    shippingOptions,
    topRatedAuctionHouses,
    totalCatalogsFound,
    whiteLabel,
}: Props) => {
    const formatMessage = useAppFormatMessage();
    useAverageLotValueActionCalendarRanking();

    return (
        <div className="flex w-full flex-col justify-center">
            <AuctionCalendarPageHeaderMeta
                baseUrl={baseUrl}
                catalogsTodayCount={catalogsTodayCount}
                page={page}
            />
            <CompleteAccountBanner />
            <AuctionCalendarPageHeader
                queryParams={queryParams}
                totalFound={totalCatalogsFound}
            />
            <div className="relative mb-lg flex flex-1 flex-col pb-[21px]">
                <div className="max-[1200px]:ml-0 max-[1200px]:min-h-[300px] max-[1200px]:max-w-full max-[1200px]:min-w-[55%] max-[670px]:p-0 relative mx-auto flex min-h-[400px] w-full max-w-[1200px] flex-col">
                    <div className="flex flex-row">
                        <div className="z-[3] mx-sm -mt-lg mdMin:mr-[36px] mdMin:block mdMin:min-w-[200px] mdMin:flex-[0_0_10%] smMin:mr-[28px] smMin:mt-[-20px] smMin:block smMin:min-w-[200px] smMin:flex-[0_0_10%] xsMax:hidden">
                            <div className="mt-[19px]">
                                <DatePickerFacet
                                    onDateChange={dateChanged}
                                    selectedDate={selectedDate}
                                />
                            </div>
                            <MultiCheckFacet
                                checklist={saleTypes}
                                emptyMessage={formatMessage(['search_no_types_found'])}
                                facetId={FacetTypes.SALE_TYPE_ID}
                                facetOptionChecked={facetOptionChecked}
                                queryParams={queryParams}
                                showSearchHeader={false}
                                title={String(formatMessage(['search_facet_title_sale_type']))}
                            />
                            {topRatedAuctionHouses.length > 0 && !whiteLabel ? (
                                <MultiCheckFacet
                                    checklist={topRatedAuctionHouses}
                                    emptyMessage={formatMessage(['search_no_types_found'])}
                                    facetId={FacetTypes.TOP_RATED_ID}
                                    facetOptionChecked={facetOptionChecked}
                                    queryParams={queryParams}
                                    showSearchHeader={false}
                                    title={String(formatMessage(['auctionCalendar', 'houseRating']))}
                                />
                            ) : null}
                            <MultiCheckFacet
                                checklist={shippingOptions}
                                emptyMessage={formatMessage(['search_no_shipping_options_found'])}
                                facetId={FacetTypes.SHIPPING}
                                facetOptionChecked={facetOptionChecked}
                                queryParams={queryParams}
                                showSearchHeader={false}
                                title={String(formatMessage(['auctionCalendar', 'shippingOptions']))}
                            />
                            <MultiCheckFacet
                                checklist={liveBroadcastOptions}
                                emptyMessage={formatMessage(['search_no_audio_video_found'])}
                                facetId={FacetTypes.AUDIO_VIDEO}
                                facetOptionChecked={facetOptionChecked}
                                queryParams={queryParams}
                                showSearchHeader={false}
                                title={String(formatMessage(['auctionCalendar', 'liveBroadcast']))}
                            />
                            {!whiteLabel && (
                                <MultiCheckFacet
                                    checklist={auctionHouses}
                                    emptyMessage={formatMessage(['search_no_auction_house_found'])}
                                    facetId={FacetTypes.AUCTION_HOUSE_ID}
                                    facetOptionChecked={facetOptionChecked}
                                    queryParams={queryParams}
                                    searchPlaceholder={String(formatMessage(['searchByHousePlaceholder']))}
                                    showSearchHeader
                                    title={String(formatMessage(['auction_house']))}
                                />
                            )}
                            <MultiCheckFacet
                                checklist={countries}
                                emptyMessage={formatMessage(['search_no_country_found'])}
                                facetId={FacetTypes.COUNTRY_CODE_ID}
                                facetOptionChecked={facetOptionChecked}
                                queryParams={queryParams}
                                showSearchHeader={false}
                                title={String(formatMessage(['countryCapitalized']))}
                            />
                            {!whiteLabel && (
                                <AdContainer
                                    pageName="calendar"
                                    pathname={pathname}
                                    queryParams={queryParams}
                                />
                            )}
                        </div>

                        <div className="w-full">
                            <AuctionCalendarResults
                                catalogIds={catalogIds}
                                changePageAndNavigate={changePageAndNavigate}
                                changePageSizeAndNavigate={changePageSizeAndNavigate}
                                page={page}
                                pageSize={pageSize}
                                totalItemsFound={totalCatalogsFound}
                            />
                        </div>
                        {searchSubmitted && (
                            <div className="min-[375px]:pt-[100px] min-[976px]:pt-[200px] absolute right-0 mt-0 flex h-full w-full justify-center bg-background-primary/80">
                                <Loading size={64} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const AuctionCalendarPage = memo<Props>(AuctionCalendarPageComponent, isEqual);
